import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePark } from "@/contexts/ParkContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  LayoutDashboard, Users, Map, CalendarRange, Receipt,
  FileBarChart, Settings, LogOut, ParkingSquare, Wrench, UserCog, Building2, Globe2, ShieldAlert, BookOpen,
} from "lucide-react";
import { API } from "@/lib/apiClient";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/tenants", label: "Tenants", icon: Users, testId: "nav-tenants" },
  { to: "/lots", label: "Lots", icon: ParkingSquare, testId: "nav-lots" },
  { to: "/park-map", label: "Park Map", icon: Map, testId: "nav-park-map" },
  { to: "/reservations", label: "Reservations", icon: CalendarRange, testId: "nav-reservations" },
  { to: "/invoices", label: "Billing", icon: Receipt, testId: "nav-invoices" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testId: "nav-reports" },
];

export function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { parks, currentPark, switchPark } = usePark();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("pd:current_park_id");
    navigate("/login");
  };

  const isPark = ["master", "park_admin", "super_admin", "admin"].includes(user?.role);
  const isSuper = ["master", "super_admin", "admin"].includes(user?.role);
  const isMaster = user?.role === "master";

  // Items render with bigger touch targets on mobile (h-11 ≈ 44px Apple HIG).
  const itemClasses = ({ isActive }) =>
    `nav-item flex items-center gap-3 px-3 py-2.5 md:py-2 min-h-[44px] md:min-h-0 rounded-lg text-sm ${
      isActive
        ? "bg-[#F0F0EC] text-[#284B3B] font-medium"
        : "text-[#1A211D] hover:bg-[#F7F7F4]"
    }`;

  return (
    <aside className="w-72 md:w-64 shrink-0 border-r border-[#EAEAE5] bg-white h-full md:h-screen md:sticky md:top-0 flex flex-col min-h-0" data-testid="app-sidebar">
      <div className="px-6 py-5 border-b border-[#EAEAE5]">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-lg bg-[#284B3B] flex items-center justify-center text-white">
            <ParkingSquare size={18} strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-tight text-[#1A211D]" style={{ fontFamily: "Outfit" }}>Park Director</div>
            <div className="text-[10px] tracking-widest uppercase text-[#737A76]">Resort Manager</div>
          </div>
        </div>
        {/* Park switcher */}
        {parks.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76] mb-1">Current Park</div>
            <Select value={currentPark?.id || ""} onValueChange={switchPark}>
              <SelectTrigger className="h-9 bg-[#FAFAF7] border-[#EAEAE5] text-sm" data-testid="park-switcher">
                <SelectValue placeholder="Select park…" />
              </SelectTrigger>
              <SelectContent>
                {parks.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`park-option-${p.id}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testId}
            onClick={onNavigate}
            className={itemClasses}
          >
            <Icon size={17} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}

        {isPark && (
          <>
            <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest text-[#737A76]">Admin</div>
            <NavLink to="/settings" data-testid="nav-settings" onClick={onNavigate} className={itemClasses}>
              <Settings size={17} strokeWidth={1.8} /><span>Park Setup</span>
            </NavLink>
            <NavLink to="/admin-tools" data-testid="nav-admin-tools" onClick={onNavigate} className={itemClasses}>
              <Wrench size={17} strokeWidth={1.8} /><span>Admin Tools</span>
            </NavLink>
          </>
        )}

        {isSuper && (
          <>
            <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest text-[#737A76]">Super Admin</div>
            <NavLink to="/agency" data-testid="nav-agency" onClick={onNavigate} className={itemClasses}>
              <Globe2 size={17} strokeWidth={1.8} /><span>Agency</span>
            </NavLink>
            <NavLink to="/parks" data-testid="nav-parks" onClick={onNavigate} className={itemClasses}>
              <Building2 size={17} strokeWidth={1.8} /><span>Parks</span>
            </NavLink>
            <NavLink to="/users" data-testid="nav-users" onClick={onNavigate} className={itemClasses}>
              <UserCog size={17} strokeWidth={1.8} /><span>Users</span>
            </NavLink>
          </>
        )}

        {isMaster && (
          <>
            <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest text-[#A13E34]">Master</div>
            <NavLink
              to="/master-tools"
              data-testid="nav-master-tools"
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 md:py-2 min-h-[44px] md:min-h-0 rounded-lg text-sm ${
                  isActive ? "bg-[#FFE4E1] text-[#A13E34] font-medium" : "text-[#A13E34] hover:bg-[#FFE4E1]/60"
                }`
              }
            >
              <ShieldAlert size={17} strokeWidth={1.8} /><span>Master Tools</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="border-t border-[#EAEAE5] px-3 py-3 shrink-0" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <a
          href={`${API}/docs/owners-manual.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-owners-manual"
          className="flex items-center gap-3 px-3 py-2.5 md:py-2 min-h-[44px] md:min-h-0 rounded-lg text-sm text-[#1A211D] hover:bg-[#F7F7F4]"
        >
          <BookOpen size={17} strokeWidth={1.8} /><span>Owner&apos;s manual</span>
        </a>
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#B5655C] text-white flex items-center justify-center text-xs font-semibold uppercase">
            {user?.name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#737A76]" data-testid="user-role">{user?.role?.replace("_", " ")}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button" className="p-1.5 rounded-md hover:bg-[#F7F7F4] text-[#737A76]" title="Log out">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
